// Translations for VF ACTIONS - French/Breton bilingual support

export const translations = {
  fr: {
    // Header
    nav: {
      platforms: "Solutions",
      services: "Services",
      partners: "Communauté",
      news: "Actualités",
      references: "Démonstrations",
      booking: "Rendez-vous",
      contact: "Contact",
      cta: "Nous rejoindre"
    },
    
    // Hero
    hero: {
      subtitle: "Artisans · Commerçants · TPE",
      title1: "Votre métier, c'est votre art.",
      title2: "Le numérique, c'est le nôtre.",
      description: "VF ACTIONS met l'intelligence artificielle au service des artisans, commerçants et petites entreprises de Bretagne : communication, visibilité et pilotage simplifiés, pour rester concentré sur votre métier. Et demain, les outils des collectivités.",
      ctaPrimary: "Réserver mon diagnostic gratuit",
      ctaSecondary: "Voir nos solutions"
    },
    
    // Metrics
    metrics: {
      carbon: "Solutions",
      carbonDesc: "Déjà en ligne",
      compliance: "Made in Bretagne",
      complianceDesc: "Conception locale",
      biodiversity: "Diagnostic gratuit",
      biodiversityDesc: "Sans engagement",
      circular: "Réactivité",
      circularDesc: "Réponse sous 24h"
    },
    
    // Compliance badges / marquee
    compliance: {
      alignedOn: "Nos engagements",
      paris: "Artisans & Commerçants",
      csrd: "Intelligence Artificielle",
      taxonomy: "TPE & PME",
      biodiversity: "Numérique responsable"
    },
    
    // Video section
    video: {
      subtitle: "La société en vidéo",
      title: "Mesurer. Décider. Agir.",
      description: "40 secondes pour découvrir comment VF ACTIONS met l'IA au profit des territoires : des outils concrets qui font avancer tout le monde.",
      play: "Lancer la vidéo"
    },

    // Apps section
    apps: {
      subtitle: "Nos solutions",
      title: "Des outils concrets pour votre activité",
      description: "Des plateformes simples, pensées pour les petites structures — et demain pour les collectivités.",
      accessPlatform: "Accéder à la plateforme",
      learnMore: "En savoir plus",
      statusProduction: "Production",
      statusBeta: "Beta",
      statusDev: "Développement",
      statusSoon: "Bientôt"
    },
    
    // Use cases section
    useCases: {
      subtitle: "Scénarios d'usage",
      title: "L'IA, ça changerait quoi au quotidien ?",
      description: "Trois scénarios fictifs pour visualiser concrètement ce que nos solutions pourraient changer.",
      note: "Scénarios fictifs présentés à titre d'illustration — ils ne décrivent ni clients existants, ni résultats obtenus.",
      fictional: "Scénario fictif",
      before: "Aujourd'hui",
      after: "Avec VF ACTIONS",
      cta: "Voir les démonstrations",
      cases: {
        habitant: {
          tag: "Pour un artisan",
          title: "Un menuisier type, personnage fictif",
          before: "Des devis le soir, zéro temps pour les réseaux, un site vieillissant : les clients passent à côté.",
          after: "Son site serait remis à niveau et l'IA publierait régulièrement ses réalisations. Les demandes de devis arriveraient seules.",
          result: "Objectif : plus de demandes de devis, sans effort de com'"
        },
        commune: {
          tag: "Pour une commune",
          title: "Une commune type de 4 500 habitants",
          before: "Des données éparpillées, des décisions difficiles à objectiver face aux habitants.",
          after: "Un tableau de bord unique — énergie, budget, services. L'équipe municipale déciderait plus vite et expliquerait mieux ses choix.",
          result: "Objectif : des décisions plus rapides et mieux expliquées"
        },
        commerce: {
          tag: "Pour un commerce",
          title: "Une boulangerie type de centre-ville",
          before: "Pas le temps de communiquer : réseaux sociaux délaissés, clients mal informés des nouveautés.",
          after: "L'IA rédigerait publications et offres en quelques secondes. Le commerçant garde la main, l'IA fait le reste.",
          result: "Objectif : plus de visibilité, sans y passer ses soirées"
        }
      }
    },

    // References page
    references: {
      kicker: "Démonstrations",
      title: "Ce que nos solutions peuvent faire",
      intro: "Trois scénarios de démonstration, construits à partir de besoins réels observés sur le terrain, pour projeter concrètement l'apport de nos solutions. VF ACTIONS est une jeune société : ces cas sont fictifs — vous pourriez être notre première référence.",
      note: "Ces scénarios sont fictifs et présentés à titre démonstratif. Ils ne décrivent ni clients existants, ni déploiements réels, ni résultats obtenus.",
      fictional: "Scénario fictif",
      challenge: "Le défi type",
      solution: "Notre approche",
      results: "Bénéfices visés",
      platform: "Solution concernée",
      ctaTitle: "Devenez notre première référence",
      ctaText: "Rejoignez les organisations qui veulent prendre une longueur d'avance avec l'IA territoriale.",
      ctaBtn: "Discuter de votre projet",
      back: "Retour au site",
      cases: {
        c1: {
          sector: "Collectivité",
          title: "Piloter l'énergie des bâtiments d'une commune",
          location: "Scénario de démonstration · commune littorale type",
          challenge: "Des consommations d'énergie éparpillées entre bâtiments publics, sans vision globale ni leviers d'action clairs pour l'équipe municipale.",
          solution: "Déployer un tableau de bord unique : données énergétiques centralisées, alertes automatiques et priorisation des actions par l'IA.",
          results: ["Réduire la consommation des bâtiments suivis", "Une vision unique de l'ensemble du parc public", "Des décisions budgétaires objectivées en conseil"],
          platform: "Données & Pilotage"
        },
        c2: {
          sector: "Entreprise",
          title: "Automatiser la communication d'une PME",
          location: "Scénario de démonstration · PME agroalimentaire type",
          challenge: "Aucun temps pour communiquer : réseaux sociaux à l'abandon, offres invisibles, notoriété locale en berne.",
          solution: "Mettre en place VF ACTIONS Marketing IA : génération de contenus adaptés au ton de la marque, planification et SEO intégré.",
          results: ["Plusieurs heures gagnées chaque semaine", "Une présence en ligne régulière", "Un ton de marque maîtrisé"],
          platform: "VF ACTIONS — Marketing IA"
        },
        c3: {
          sector: "Association",
          title: "Réaliser le premier bilan carbone d'un réseau",
          location: "Scénario de démonstration · réseau associatif type",
          challenge: "Un réseau multi-sites sans méthode ni outil pour mesurer son empreinte et prioriser ses actions.",
          solution: "Accompagner avec Carbon Impact : collecte guidée des données et calcul automatisé selon la méthodologie Bilan Carbone®.",
          results: ["Un premier bilan complet en quelques semaines", "Tous les sites cartographiés", "Un plan d'action partagé"],
          platform: "Carbon Impact"
        }
      }
    },

    // Services section
    services: {
      subtitle: "Notre accompagnement",
      title: "On s'occupe du numérique, vous gardez la main",
      description: "Des services pensés pour les journées déjà bien remplies."
    },
    
    // Partners section
    partners: {
      subtitle: "Rejoignez-nous",
      title: "Construit pour les petites structures",
      description: "VF ACTIONS accompagne d'abord les artisans, commerçants et TPE. Associations et collectivités : nos outils se construisent aussi avec vous, pour demain.",
      cta: "Nous rejoindre",
      benefitsTitle: "Ce que nous vous apportons",
      benefits: {
        visibility: { title: "Visibilité locale", desc: "Vos clients vous trouvent : présence en ligne régulière, sans y passer vos soirées" },
        reporting: { title: "Temps gagné", desc: "L'IA rédige, planifie et publie — vous validez en deux minutes" },
        coconstruction: { title: "Co-construction", desc: "Des outils développés au plus près de votre métier" },
        network: { title: "Réseau local", desc: "Connexion avec un écosystème d'acteurs bretons qui avancent" }
      },
      types: {
        collectivities: { name: "Artisans", type: "Métiers de l'artisanat" },
        companies: { name: "Commerçants & TPE", type: "Boutiques, restaurants, services" },
        associations: { name: "Associations", type: "Professionnelles et citoyennes" },
        institutions: { name: "Collectivités", type: "Notre vision à long terme" }
      }
    },
    
    // About section
    about: {
      subtitle: "Qui sommes-nous ?",
      title: "Une jeune société qui vise haut",
      p1: "<strong>VF ACTIONS est une société nouvelle, née en Bretagne</strong>, avec une ambition claire : devenir le partenaire numérique de référence des artisans, commerçants et petites entreprises.",
      p2: "<strong>Notre conviction</strong> : l'IA n'est pas réservée aux grands groupes. Elle doit profiter aux petites structures qui font vivre nos territoires — simplement, concrètement, sans jargon.",
      p3: "<strong>Notre différence</strong> : un numérique performant, sobre et responsable. Et une vision à long terme : étendre demain nos outils aux collectivités du territoire.",
      rgpd: "Vos données restent confidentielles et ne sont jamais partagées.",
      location: "Bretagne, France"
    },
    
    // News section
    news: {
      subtitle: "Actualités",
      title: "Ça avance chez VF ACTIONS",
      description: "Nos dernières avancées : produits, événements et partenariats.",
      empty: "Aucune actualité pour le moment."
    },

    // Booking section
    booking: {
      subtitle: "Diagnostic gratuit",
      title: "Réservez votre diagnostic gratuit",
      description: "30 minutes pour faire le point sur votre activité — artisan, commerçant, TPE — et repartir avec des pistes concrètes. Sans engagement.",
      options: {
        discovery: { title: "Diagnostic numérique gratuit", duration: "30 min", desc: "Un état des lieux concret de votre situation et des pistes activables immédiatement. Pour tous les profils." },
        partnership: { title: "Réunion partenariat", duration: "45 min", desc: "Discussion approfondie sur les modalités de collaboration et de partenariat." },
        demo: { title: "Démo plateforme", duration: "30 min", desc: "Présentation détaillée de nos outils et fonctionnalités." }
      },
      bookSlot: "Réserver ce créneau",
      videoNote: "Visioconférence via Google Meet ou Microsoft Teams selon votre préférence"
    },
    
    // FAQ section
    faq: {
      subtitle: "Questions fréquentes",
      title: "On répond à vos questions",
      description: "Tout ce qu'il faut savoir avant de se lancer — sans jargon.",
      cta: "Une autre question ? Contactez-nous",
      items: {
        q1: { q: "Qu'est-ce que l'IA territoriale ?", a: "C'est l'intelligence artificielle appliquée aux besoins concrets d'un territoire : simplifier les démarches des habitants, aider les communes à piloter leurs données, faire gagner du temps aux entreprises et aux associations. Des outils simples, utiles au quotidien." },
        q2: { q: "À qui s'adressent vos solutions ?", a: "D'abord aux artisans, commerçants et petites entreprises. Les associations peuvent aussi nous solliciter, et nos outils pour les collectivités se construisent pour demain." },
        q3: { q: "Faut-il des compétences techniques ?", a: "Non. Nos outils sont conçus pour être pris en main en quelques minutes, et nous vous accompagnons à chaque étape." },
        q4: { q: "Combien ça coûte ?", a: "Le diagnostic de 30 minutes est gratuit et sans engagement. Ensuite, chaque solution dispose d'une tarification adaptée à votre profil et à votre taille. Parlons-en lors du diagnostic." },
        q5: { q: "Mes données sont-elles protégées ?", a: "Oui. Vos données restent confidentielles, hébergées en Europe, et ne sont jamais partagées avec des tiers." }
      }
    },
    
    // Contact section
    contact: {
      subtitle: "Contactez-nous",
      title: "Parlons de votre projet",
      description: "Une question, un projet ? Artisan, commerçant, TPE — notre équipe vous répond sous 24h.",
      email: "Email",
      headquarters: "Siège social",
      form: {
        name: "Nom complet",
        namePlaceholder: "Votre nom",
        email: "Email",
        emailPlaceholder: "votre@email.com",
        organization: "Structure",
        organizationPlaceholder: "Entreprise, collectivité, association...",
        orgType: "Vous êtes",
        message: "Message",
        messagePlaceholder: "Partagez votre projet, vos besoins ou vos questions...",
        submit: "Envoyer le message",
        submitting: "Envoi en cours...",
        success: "Message envoyé avec succès ! Nous vous répondrons rapidement.",
        error: "Erreur lors de l'envoi. Veuillez réessayer.",
        reassurance: "Réponse garantie sous 24h — sans engagement.",
        consent: "J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la",
        consentLink: "politique de confidentialité"
      },
      orgTypes: {
        artisan: "Un artisan",
        commerce: "Un commerce",
        collectivity: "Une collectivité",
        company: "Une entreprise",
        association: "Une association",
        institution: "Un particulier",
        other: "Autre"
      }
    },
    
    // Footer
    footer: {
      description: "VF ACTIONS met l'intelligence artificielle au service des artisans, commerçants et petites entreprises — et prépare les outils des collectivités de demain. Une société bretonne, ambitieuse et responsable.",
      navigation: "Navigation",
      contact: "Contact",
      form: "Formulaire",
      legal: "Mentions légales",
      privacy: "Confidentialité",
      rgpd: "",
      rights: "Tous droits réservés"
    },
    
    // Cookie banner
    cookies: {
      text: "Nous utilisons uniquement des cookies de mesure d'audience pour améliorer le site. Vous pouvez accepter ou refuser — la navigation reste identique dans les deux cas.",
      accept: "Accepter",
      refuse: "Refuser",
      more: "En savoir plus"
    },
    
    // Common
    common: {
      loading: "Chargement..."
    }
  },
  
  br: {
    // Header - Breton
    nav: {
      platforms: "Diskoulmoù",
      services: "Servijoù",
      partners: "Kumuniezh",
      news: "Keleier",
      references: "Diskouezadegoù",
      booking: "Emgav",
      contact: "Darempred",
      cta: "Dont ganeomp"
    },
    
    // Hero - Breton
    hero: {
      subtitle: "Artizaned · Stalierien · TPE",
      title1: "Ho micher eo hoc'h arz.",
      title2: "An niverel eo hon hini.",
      description: "VF ACTIONS a laka an naouegezh artifisiel e servij artizaned, stalierien hag embregerezhioù bihan Breizh : kehentiñ, gweluster ha renerezh aesaet, evit chom fokuset war ho micher. Ha warc'hoazh, ostilhoù ar strollegezhioù.",
      ctaPrimary: "Mirout va diagnostik digoust",
      ctaSecondary: "Gwelet hon diskoulmoù"
    },
    
    // Metrics - Breton
    metrics: {
      carbon: "Diskoulmoù",
      carbonDesc: "Enlinenn dija",
      compliance: "Graet e Breizh",
      complianceDesc: "Krouidigezh lec'hel",
      biodiversity: "Diagnostik digoust",
      biodiversityDesc: "Hep engouestl",
      circular: "Buanded",
      circularDesc: "Respont a-barzh 24e"
    },
    
    // Compliance badges / marquee - Breton
    compliance: {
      alignedOn: "Hon enrolladennoù",
      paris: "Artizaned & Stalierien",
      csrd: "Naouegezh Artifisiel",
      taxonomy: "TPE & PME",
      biodiversity: "Niverel kiriek"
    },
    
    // Video section - Breton
    video: {
      subtitle: "An embregerezh e video",
      title: "Muzuliañ. Divizout. Ober.",
      description: "40 eilenn evit dizoleiñ penaos e laka VF ACTIONS an IA e servij an tiriadoù : ostilhoù fetis a laka an holl da vont war-raok.",
      play: "Lañsañ ar video"
    },

    // Apps section - Breton
    apps: {
      subtitle: "Hon diskoulmoù",
      title: "Ostilhoù fetis evit hoc'h obererezh",
      description: "Savennoù simpl, soñjet evit ar frammoù bihan — ha warc'hoazh evit ar strollegezhioù.",
      accessPlatform: "Mont d'ar savenn",
      learnMore: "Gouzout muioc'h",
      statusProduction: "Produiñ",
      statusBeta: "Beta",
      statusDev: "Diorren",
      statusSoon: "A zeu"
    },
    
    // Use cases section - Breton
    useCases: {
      subtitle: "Senarioioù implij",
      title: "An IA, petra a cheñchfe bemdez ?",
      description: "Tri senario faltazi evit gwelet fetis ar pezh a c'hellfe cheñch hon diskoulmoù.",
      note: "Senarioioù faltazi kinniget evit skeudennañ hepken — ne ziskrivont na pratikoù a zo, na disoc'hoù tapet.",
      fictional: "Senario faltazi",
      before: "Hiziv",
      after: "Gant VF ACTIONS",
      cta: "Gwelet an diskouezadegoù",
      cases: {
        habitant: {
          tag: "Evit un artizan",
          title: "Ur munuzer skouer, personaj faltazi",
          before: "Devizoù da noz, amzer ebet evit ar rouedadoù, ul lec'hienn kozh : ar pratikoù a dremen e-biou.",
          after: "E lec'hienn a vefe nevesaet hag an IA a embannfe ingal e oberennoù. Ar goulennoù deviz a zegouezhfe o-unan.",
          result: "Pal : muioc'h a c'houlennoù deviz, hep strivañ war ar c'hehentiñ"
        },
        commune: {
          tag: "Evit ur gumun",
          title: "Ur gumun skouer 4 500 annezad",
          before: "Roadoù strewet, divizoù diaes da zisplegañ dirak an annezidi.",
          after: "Un daolenn-vord nemetken — energiezh, budjed, servijoù. Skipailh an ti-kêr a zivizfe buanoc'h hag a zisplegfe gwelloc'h e zibaboù.",
          result: "Pal : divizoù buanoc'h ha displeget gwelloc'h"
        },
        commerce: {
          tag: "Evit ur stal",
          title: "Ur varaerezh skouer kreiz-kêr",
          before: "Amzer ebet evit kehentiñ : rouedadoù sokial dilezet, pratikoù fall kelaouet.",
          after: "An IA a skrivfe embannadurioù ha kinnigoù en un nebeud eilennoù. Ar stalier a vir an dorn, an IA a ra ar peurrest.",
          result: "Pal : muioc'h a weluster, hep koll e nozvezhioù"
        }
      }
    },

    // References page - Breton
    references: {
      kicker: "Diskouezadegoù",
      title: "Ar pezh a c'hell ober hon diskoulmoù",
      intro: "Tri senario diskouez, savet diwar ezhommoù gwir gwelet war an dachenn, evit gwelet fetis talvoudegezh hon diskoulmoù. VF ACTIONS a zo un embregerezh yaouank : faltazi eo an degouezhioù-mañ — c'hwi a c'hellfe bezañ hon dave kentañ.",
      note: "Faltazi eo ar senarioioù-mañ, kinniget evit diskouez hepken. Ne ziskrivont na pratikoù a zo, na staliadurioù gwir, na disoc'hoù tapet.",
      fictional: "Senario faltazi",
      challenge: "An dae skouer",
      solution: "Hon doare",
      results: "Gounidoù sellet",
      platform: "Diskoulm a denn d'an dra",
      ctaTitle: "Deuit da vezañ hon dave kentañ",
      ctaText: "Deuit gant an aozadurioù a fell dezho kemer un tamm araok gant an IA tiriadel.",
      ctaBtn: "Komz eus ho raktres",
      back: "Distreiñ d'al lec'hienn",
      cases: {
        c1: {
          sector: "Strollegezh",
          title: "Renerezh energiezh savadurioù ur gumun",
          location: "Senario diskouez · kumun aod skouer",
          challenge: "Bevezadurioù energiezh strewet etre savadurioù publik, hep gweledigezh hollek na levier ober sklaer evit skipailh an ti-kêr.",
          solution: "Staliañ un daolenn-vord nemetken : roadoù energiezh kreizennet, diwalloù emgefre ha renkañ an oberoù gant an IA.",
          results: ["Digreskiñ bevezadur ar savadurioù heuliet", "Ur gweledigezh nemetken eus an holl barc publik", "Divizoù budjed displeget er c'huzul"],
          platform: "Roadoù & Renerezh"
        },
        c2: {
          sector: "Embregerezh",
          title: "Emgefreañ kehentiñ ur PME",
          location: "Senario diskouez · PME boued skouer",
          challenge: "Amzer ebet evit kehentiñ : rouedadoù sokial dilezet, kinnigoù diwelus, brud lec'hel izel.",
          solution: "Staliañ VF ACTIONS Marketing IA : krouiñ danvezioù doujus ouzh ton ar merk, steuñviñ ha SEO enframmet.",
          results: ["Meur a eurvezh gounezet bep sizhun", "Ur bezañs enlinenn reoliek", "Un ton merk mestroniet"],
          platform: "VF ACTIONS — Marketing IA"
        },
        c3: {
          sector: "Kevredigezh",
          title: "Ober bilañs karbon kentañ ur rouedad",
          location: "Senario diskouez · rouedad kevredigezhel skouer",
          challenge: "Ur rouedad lies-lec'h hep metodenn nag ostilh evit muzuliañ e roud ha renkañ e oberoù.",
          solution: "Skoazell gant Carbon Impact : dastum heñchet ar roadoù ha jediñ emgefre hervez metodologiezh Bilan Carbone®.",
          results: ["Ur bilañs klok kentañ en un nebeud sizhunioù", "An holl lec'hioù kartennet", "Ur steuñv ober rannet"],
          platform: "Carbon Impact"
        }
      }
    },

    // Services section - Breton
    services: {
      subtitle: "Hon heuliadur",
      title: "Ni a ra war-dro an niverel, c'hwi a vir an dorn",
      description: "Servijoù soñjet evit devezhioù leun dija."
    },
    
    // Partners section - Breton
    partners: {
      subtitle: "Deuit ganeomp",
      title: "Savet evit ar frammoù bihan",
      description: "VF ACTIONS a ambroug da gentañ an artizaned, ar stalierien hag an TPE. Kevredigezhioù ha strollegezhioù : hon ostilhoù a vez savet ivez ganeoc'h, evit warc'hoazh.",
      cta: "Dont ganeomp",
      benefitsTitle: "Ar pezh a zegasomp deoc'h",
      benefits: {
        visibility: { title: "Gweluster lec'hel", desc: "Ho pratikoù ho kav : bezañs enlinenn reoliek, hep koll ho nozvezhioù" },
        reporting: { title: "Amzer gounezet", desc: "An IA a skriv, a steuñv hag a embann — c'hwi a gadarna e div vunutenn" },
        coconstruction: { title: "Kensevel", desc: "Ostilhoù diorroet tost-tre d'ho micher" },
        network: { title: "Rouedad lec'hel", desc: "Stag gant un ekosistem a oberourien vreizhek a ya war-raok" }
      },
      types: {
        collectivities: { name: "Artizaned", type: "Micherioù an artizanerezh" },
        companies: { name: "Stalierien & TPE", type: "Stalioù, pretioù, servijoù" },
        associations: { name: "Kevredigezhioù", type: "A-vicher ha keodedel" },
        institutions: { name: "Strollegezhioù", type: "Hon gweledigezh hirdermen" }
      }
    },
    
    // About section - Breton
    about: {
      subtitle: "Piv omp ?",
      title: "Un embregerezh yaouank uhel e vennad",
      p1: "<strong>VF ACTIONS a zo un embregerezh nevez, ganet e Breizh</strong>, gant ur mennad sklaer : dont da vezañ keveler niverel dave an artizaned, ar stalierien hag an embregerezhioù bihan.",
      p2: "<strong>Hon c'hredenn</strong> : an IA n'eo ket miret evit ar strolladoù bras. Talvezout a rank d'ar frammoù bihan a laka hon tiriadoù da vevañ — simpl, fetis, hep yezhoù teknik.",
      p3: "<strong>Hon diforc'h</strong> : un niverel efedus, sober ha kiriek. Hag ur weledigezh hirdermen : astenn warc'hoazh hon ostilhoù d'ar strollegezhioù.",
      rgpd: "Ho roadoù a chom kuzh ha n'int morse rannet.",
      location: "Breizh, Bro-C'hall"
    },
    
    // News section - Breton
    news: {
      subtitle: "Keleier",
      title: "Mont a ra war-raok e VF ACTIONS",
      description: "Hon avansoù diwezhañ : produioù, darvoudoù ha kevelerezhioù.",
      empty: "Keloù ebet evit ar mare."
    },

    // Booking section - Breton
    booking: {
      subtitle: "Diagnostik digoust",
      title: "Miret ho tiagnostik digoust",
      description: "30 munutenn evit ober ar poent war hoc'h obererezh — artizan, stalier, TPE — ha mont kuit gant roudoù fetis. Hep engouestl.",
      options: {
        discovery: { title: "Diagnostik niverel digoust", duration: "30 min", desc: "Ur stad fetis eus ho stad ha roudoù da lakaat e pleustr diouzhtu. Evit an holl brofiloù." },
        partnership: { title: "Emvod kevelerezh", duration: "45 min", desc: "Diskouezh-don war modalelezhoù ar c'hevelererezh." },
        demo: { title: "Demo savenn", duration: "30 min", desc: "Kinnig munud hon ostilhoù hag hon arc'hwelioù." }
      },
      bookSlot: "Mirout ar plas-mañ",
      videoNote: "Video-emvod dre Google Meet pe Microsoft Teams hervez ho teurel"
    },
    
    // FAQ section - Breton
    faq: {
      subtitle: "Goulennoù stank",
      title: "Respont a reomp d'ho koulennoù",
      description: "Kement a zo da c'houzout a-raok en em lañsañ — hep yezhoù teknik.",
      cta: "Ur goulenn all ? Kit e darempred",
      items: {
        q1: { q: "Petra eo an IA tiriadel ?", a: "An naouegezh artifisiel arloet da ezhommoù fetis un tiriad eo : aesaat kantouezhioù an annezidi, sikour ar c'humunioù da renerezh o roadoù, lakaat an embregerezhioù hag ar c'hevredigezhioù da c'hounit amzer. Ostilhoù simpl, talvoudus bemdez." },
        q2: { q: "Da biv eo graet ho tiskoulmoù ?", a: "Da gentañ d'an artizaned, d'ar stalierien ha d'an embregerezhioù bihan. Ar c'hevredigezhioù a c'hell ivez goulenn diganeomp, hag hon ostilhoù evit ar strollegezhioù a vez savet evit warc'hoazh." },
        q3: { q: "Ezhomm a zo barregezhioù teknikel ?", a: "Nann. Hon ostilhoù a zo soñjet evit bezañ kemeret e dorn en un nebeud munutennoù, hag ho ambroug a reomp da bep pazenn." },
        q4: { q: "Pegement e koust ?", a: "An diagnostik 30 munutenn a zo digoust hag hep engouestl. Goude, pep diskoulm en deus prizioù azasaet d'ho profil ha d'ho ment. Komzomp eus se e-pad an diagnostik." },
        q5: { q: "Ha gwarezet eo va roadoù ?", a: "Ya. Ho roadoù a chom kuzh, herberc'hiet en Europa, ha n'int morse rannet gant trede tudoù." }
      }
    },
    
    // Contact section - Breton
    contact: {
      subtitle: "Darempredoù",
      title: "Komzomp eus ho raktres",
      description: "Ur goulenn, ur raktres ? Artizan, stalier, TPE — hon skipailh a respont deoc'h a-barzh 24e.",
      email: "Postel",
      headquarters: "Penndi",
      form: {
        name: "Anv klok",
        namePlaceholder: "Hoc'h anv",
        email: "Postel",
        emailPlaceholder: "ho@bostel.com",
        organization: "Framm",
        organizationPlaceholder: "Embregererezh, kumulun, kevredigezh...",
        orgType: "Oc'h",
        message: "Kemennadenn",
        messagePlaceholder: "Rannit ho raktres, ho tezirioù pe ho koulennoù...",
        submit: "Kas ar gemennadenn",
        submitting: "O kas...",
        success: "Kemennadenn kaset gant berzh! Respontit a raimp buan.",
        error: "Fazi en ur gas. Klaskit en-dro mar plij.",
        reassurance: "Respont gwarantet a-barzh 24e — hep engouestl.",
        consent: "Asantiñ a ran e vefe implijet va roadoù evit ober war-dro va goulenn, hervez ar",
        consentLink: "reolennoù kuzuliegezh"
      },
      orgTypes: {
        artisan: "Un artizan",
        commerce: "Ur stal",
        collectivity: "Ur gumulun",
        company: "Un embregererezh",
        association: "Ur gevredigezh",
        institution: "Un den prevez",
        other: "All"
      }
    },
    
    // Footer - Breton
    footer: {
      description: "VF ACTIONS a laka an naouegezh artifisiel e servij an artizaned, ar stalierien hag an embregerezhioù bihan — hag a brepar ostilhoù strollegezhioù warc'hoazh. Un embregerezh breizhek, uhel e vennad ha kiriek.",
      navigation: "Merdeiñ",
      contact: "Darempred",
      form: "Furmskrid",
      legal: "Menozoù lezenn",
      privacy: "Kuzuliegezh",
      rgpd: "",
      rights: "An holl wirioù miret"
    },
    
    // Cookie banner - Breton
    cookies: {
      text: "Implijout a reomp toupinoù muzuliañ arvest hepken evit gwellaat al lec'hienn. Gallout a rit asantiñ pe nac'hañ — ar merdeiñ a chom heñvel en daou zegouezh.",
      accept: "Asantiñ",
      refuse: "Nac'hañ",
      more: "Gouzout muioc'h"
    },
    
    // Common - Breton
    common: {
      loading: "O kargañ..."
    }
  }
};

export const getTranslation = (lang, path) => {
  const keys = path.split('.');
  let result = translations[lang];
  for (const key of keys) {
    result = result?.[key];
  }
  return result || path;
};
